import { EventSuccessModal } from '@/components/ui/EventSuccessModal';
import { api } from '@/lib/api';
import { formatEventDate } from '@/lib/utils';
import type { Event } from '@eventflow/shared-types';
import { type CreateEventInput, createEventSchema } from '@eventflow/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

export default function CreateEventPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [createdEvent, setCreatedEvent] = useState<Event | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateEventInput>({
    resolver: zodResolver(createEventSchema),
    defaultValues: {
      is_published: false,
    },
  });

  // Log de errores de validación
  console.log(' [CreateEvent] Validation errors:', errors);

  const onSubmit = async (data: CreateEventInput) => {
    console.log(' [CreateEvent] Form submitted with data:', data);
    setIsSubmitting(true);
    setError('');

    try {
      // Combinar fecha con hora de inicio para crear un datetime completo
      // Esto evita problemas de zona horaria al usar solo la fecha
      let eventDateISO = data.event_date;
      if (data.event_date && data.event_start_time) {
        // Crear fecha combinando fecha + hora de inicio en zona horaria local
        const combinedDateTime = `${data.event_date}T${data.event_start_time}:00`;
        eventDateISO = new Date(combinedDateTime).toISOString();
      } else if (data.event_date) {
        // Fallback: agregar mediodía para evitar problemas de zona horaria
        eventDateISO = new Date(`${data.event_date}T12:00:00`).toISOString();
      }

      const payload = {
        ...data,
        event_date: eventDateISO,
      };

      console.log(' [CreateEvent] Calling API to create event...');
      console.log(' [CreateEvent] Payload:', payload);
      const event = await api.post<Event>('/events', payload);
      console.log(' [CreateEvent] Event created successfully:', event);
      console.log(' [CreateEvent] Event ID:', event.id);

      // Mostrar modal de éxito en lugar de alert
      setCreatedEvent(event);
      setShowSuccessModal(true);
      setIsSubmitting(false);
    } catch (err) {
      console.error(' [CreateEvent] Error creating event:', err);
      setError(err instanceof Error ? err.message : 'Error al crear evento');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/events')}
          className="text-sm text-gray-600 hover:text-gray-900 mb-2"
        >
          ← Volver a Mis Eventos
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Crear Nuevo Evento</h1>
        <p className="text-gray-600 mt-2">
          Completa la información básica de tu evento. Después podrás crear el mapa de asientos.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="card space-y-6">
        {/* Nombre del evento */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Nombre del Evento *
          </label>
          <input
            id="name"
            type="text"
            {...register('name')}
            className="input-field"
            placeholder="Ej: Concierto de Rock 2025"
          />
          {errors.name && <p className="text-red-600 text-sm mt-1">{errors.name.message}</p>}
        </div>

        {/* Límite de confirmación de pagos */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Límite de confirmación de pagos
          </label>
          <select
            className="input-field"
            value={(() => {
              const sel = watch('payment_confirmation_deadline_hours');
              const allowed = [8, 40, 56, 120];
              if (sel === null) return 'null';
              if (sel === undefined) return '';
              return allowed.includes(Number(sel)) ? String(sel) : '';
            })()}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '') {
                setValue('payment_confirmation_deadline_hours', undefined, { shouldDirty: true });
              } else if (v === 'null') {
                setValue('payment_confirmation_deadline_hours', null, { shouldDirty: true });
              } else {
                setValue('payment_confirmation_deadline_hours', Number(v) as any, {
                  shouldDirty: true,
                });
              }
            }}
          >
            <option value="">Sin límite por ahora</option>
            <option value="null">Sin límite</option>
            <option value={8}>1 día hábil (8h)</option>
            <option value={40}>5 días hábiles (40h)</option>
            <option value={56}>7 días hábiles (56h)</option>
            <option value={120}>15 días hábiles (120h)</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Se respetan horarios laborales L-V de 8:00 a 17:00 (hora México). "Sin límite" permite
            confirmar en cualquier momento.
          </p>
          {errors.payment_confirmation_deadline_hours && (
            <p className="text-red-600 text-sm mt-1">
              {errors.payment_confirmation_deadline_hours.message}
            </p>
          )}
        </div>

        {/* Descripción */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Descripción
          </label>
          <textarea
            id="description"
            {...register('description')}
            rows={4}
            className="input-field resize-none"
            placeholder="Describe tu evento (lineup, horarios, políticas, etc.)"
          />
          {errors.description && (
            <p className="text-red-600 text-sm mt-1">{errors.description.message}</p>
          )}
        </div>

        {/* Fecha del evento */}
        <div>
          <label htmlFor="event_date" className="block text-sm font-medium text-gray-700 mb-1">
            Fecha del Evento *
          </label>
          <input
            id="event_date"
            type="date"
            {...register('event_date')}
            className="input-field"
          />
          {errors.event_date && (
            <p className="text-red-600 text-sm mt-1">{errors.event_date.message}</p>
          )}
        </div>

        {/* Horarios del evento */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="event_start_time" className="block text-sm font-medium text-gray-700 mb-1">
              Hora de Inicio *
            </label>
            <input
              id="event_start_time"
              type="time"
              {...register('event_start_time')}
              className="input-field"
            />
            {errors.event_start_time && (
              <p className="text-red-600 text-sm mt-1">{errors.event_start_time.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="event_end_time" className="block text-sm font-medium text-gray-700 mb-1">
              Hora de Fin *
            </label>
            <input
              id="event_end_time"
              type="time"
              {...register('event_end_time')}
              className="input-field"
            />
            {errors.event_end_time && (
              <p className="text-red-600 text-sm mt-1">{errors.event_end_time.message}</p>
            )}
          </div>
        </div>

        {/* Duración calculada */}
        {watch('event_start_time') && watch('event_end_time') && (() => {
          const start = watch('event_start_time');
          const end = watch('event_end_time');
          if (start && end) {
            const [startH, startM] = start.split(':').map(Number);
            const [endH, endM] = end.split(':').map(Number);
            const startMinutes = startH * 60 + startM;
            const endMinutes = endH * 60 + endM;
            const diff = endMinutes - startMinutes;
            if (diff > 0) {
              const hours = Math.floor(diff / 60);
              const minutes = diff % 60;
              return (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
                  <span className="text-blue-600"></span>
                  <span className="text-sm text-blue-800">
                    Duración del evento: <strong>{hours > 0 ? `${hours}h ` : ''}{minutes > 0 ? `${minutes}min` : ''}</strong>
                  </span>
                </div>
              );
            }
          }
          return null;
        })()}

        {/* Nombre del venue */}
        <div>
          <label htmlFor="venue_name" className="block text-sm font-medium text-gray-700 mb-1">
            Nombre del Lugar (Venue) *
          </label>
          <input
            id="venue_name"
            type="text"
            {...register('venue_name')}
            className="input-field"
            placeholder="Ej: Auditorio Nacional, Arena CDMX"
          />
          {errors.venue_name && (
            <p className="text-red-600 text-sm mt-1">{errors.venue_name.message}</p>
          )}
        </div>

        {/* Dirección del venue */}
        <div>
          <label htmlFor="venue_address" className="block text-sm font-medium text-gray-700 mb-1">
            Dirección del Lugar
          </label>
          <input
            id="venue_address"
            type="text"
            {...register('venue_address')}
            className="input-field"
            placeholder="Calle, número, colonia, ciudad, CP"
          />
          {errors.venue_address && (
            <p className="text-red-600 text-sm mt-1">{errors.venue_address.message}</p>
          )}
        </div>

        {/* URL de imagen del venue */}
        <div>
          <label htmlFor="venue_image_url" className="block text-sm font-medium text-gray-700 mb-1">
            URL de Imagen del Venue
          </label>
          <input
            id="venue_image_url"
            type="url"
            {...register('venue_image_url')}
            className="input-field"
            placeholder="https://ejemplo.com/imagen.jpg"
          />
          {errors.venue_image_url && (
            <p className="text-red-600 text-sm mt-1">{errors.venue_image_url.message}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Imagen que se mostrará en la lista de eventos (opcional)
          </p>
        </div>

        {/* Publicado */}
        <div className="flex items-center gap-3">
          <input
            id="is_published"
            type="checkbox"
            {...register('is_published')}
            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <label htmlFor="is_published" className="text-sm text-gray-700">
            Publicar evento inmediatamente
          </label>
        </div>
        <p className="text-xs text-gray-500 -mt-4 ml-7">
          Si está publicado, los clientes podrán ver el evento y comprar boletos. Puedes publicarlo
          más tarde.
        </p>

        {/* Botones */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/dashboard/events')}
            className="btn-secondary flex-1"
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button type="submit" className="btn-primary flex-1" disabled={isSubmitting}>
            {isSubmitting ? 'Creando...' : '✨ Crear Evento'}
          </button>
        </div>
      </form>

      {/* Modal de éxito */}
      {createdEvent && (
        <EventSuccessModal
          isOpen={showSuccessModal}
          onClose={() => {
            setShowSuccessModal(false);
            navigate(`/dashboard/events/${createdEvent.id}/map`);
          }}
          eventName={createdEvent.name}
          eventDate={formatEventDate(createdEvent.event_date)}
          eventStartTime={createdEvent.event_start_time}
          eventEndTime={createdEvent.event_end_time}
          eventVenue={createdEvent.venue_name}
          eventId={createdEvent.id}
          onNavigateToMap={() => navigate(`/dashboard/events/${createdEvent.id}/map`)}
        />
      )}
    </div>
  );
}

